import { useParams, useNavigate } from 'react-router-dom';
import { useState, type ReactNode } from 'react';
import { useMedicalRecord, useCloseMedicalRecord, useCreateAnamnese } from '@/hooks';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/Button';
import { Skeleton } from '@/components/ui/Skeleton';
import { useToast } from '@/providers/ToastProvider';
import { MedicalRecordStepper, type MedicalRecordStep } from '@/components/medical-records/MedicalRecordStepper';
import { DiagnosisSelector } from '@/components/medical-records/DiagnosisSelector';
import { PrescriptionEditor } from '@/components/medical-records/PrescriptionEditor';
import { ExamRequestEditor } from '@/components/medical-records/ExamRequestEditor';
import { ClinicalEvolutionEditor } from '@/components/medical-records/ClinicalEvolutionEditor';
import { SOAPEditor } from '@/components/medical-records/SOAPEditor';

export function MedicalRecordEditorPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { success, error } = useToast();

  const { data: medicalRecord, isLoading, isError } = useMedicalRecord(id || '');
  const closeRecord = useCloseMedicalRecord();
  const createAnamnese = useCreateAnamnese();

  const [currentStep, setCurrentStep] = useState<MedicalRecordStep>('identification');
  const [completedSteps, setCompletedSteps] = useState<MedicalRecordStep[]>([]);

  const isDoctor = user?.role === 'DOCTOR';
  const canEdit = medicalRecord?.status !== 'SIGNED' && medicalRecord?.status !== 'COMPLETED' && isDoctor;

  const markStepComplete = (step: MedicalRecordStep) => {
    if (!completedSteps.includes(step)) {
      setCompletedSteps([...completedSteps, step]);
    }
  };

  const handleClose = async () => {
    if (!user?.id || !medicalRecord) return;
    try {
      await closeRecord.mutateAsync({ recordId: medicalRecord.id, closedBy: user.id });
      success('Prontuário fechado com sucesso');
      navigate(isDoctor ? `/medico/prontuarios/${medicalRecord.id}` : `/medical-records/${medicalRecord.id}`);
    } catch (e) {
      error('Erro ao fechar prontuário');
      console.error(e);
    }
  };

  const handleSaveComplaint = async (data: { subjective?: string; objective?: string; assessment?: string; plan?: string }) => {
    try {
      await createAnamnese.mutateAsync({
        medicalRecordId: medicalRecord!.id,
        chiefComplaint: data.subjective || '',
        historyOfPresentIllness: data.assessment || '',
        symptoms: [],
        evolutionTime: '',
        currentMedications: [],
        allergies: [],
        previousDiseases: [],
        surgeries: [],
      });
      success('Queixa principal salva');
      markStepComplete('complaint');
      setCurrentStep('anamnese');
    } catch (e) {
      error('Erro ao salvar queixa principal');
      console.error(e);
    }
  };

  const handleSaveAnamnese = async (data: { subjective?: string; objective?: string; assessment?: string; plan?: string }) => {
    try {
      await createAnamnese.mutateAsync({
        medicalRecordId: medicalRecord!.id,
        chiefComplaint: data.subjective || '',
        historyOfPresentIllness: data.assessment || '',
        symptoms: [],
        evolutionTime: '',
        currentMedications: [],
        allergies: [],
        previousDiseases: [],
        surgeries: [],
      });
      success('Anamnese salva');
      markStepComplete('anamnese');
      setCurrentStep('physical-exam');
    } catch (e) {
      error('Erro ao salvar anamnese');
      console.error(e);
    }
  };

  const handleSavePhysicalExam = async (data: { subjective?: string; objective?: string; assessment?: string; plan?: string }) => {
    try {
      await createAnamnese.mutateAsync({
        medicalRecordId: medicalRecord!.id,
        chiefComplaint: data.subjective || '',
        historyOfPresentIllness: '',
        symptoms: [],
        evolutionTime: '',
        currentMedications: [],
        allergies: [],
        previousDiseases: [],
        surgeries: [],
      });
      success('Exame físico salvo');
      markStepComplete('physical-exam');
      setCurrentStep('diagnoses');
    } catch (e) {
      error('Erro ao salvar exame físico');
      console.error(e);
    }
  };

  if (isLoading) {
    return (
      <div className="container max-w-4xl py-6">
        <div className="animate-pulse space-y-4">
          <Skeleton className="h-8 w-1/3" />
          <Skeleton className="h-96 w-full" />
        </div>
      </div>
    );
  }

  if (isError || !medicalRecord) {
    return (
      <div className="container max-w-4xl py-6">
        <div className="text-center py-12">
          <h2 className="text-xl font-semibold text-gray-900">Prontuário não encontrado</h2>
          <Button variant="outline" onClick={() => navigate(-1)} className="mt-4">Voltar</Button>
        </div>
      </div>
    );
  }

  if (!canEdit) {
    return (
      <div className="container max-w-4xl py-6">
        <div className="text-center py-12">
          <h2 className="text-xl font-semibold text-gray-900">Prontuário bloqueado</h2>
          <p className="text-gray-500 mt-2">Este prontuário não pode ser editado no momento.</p>
          <Button variant="outline" onClick={() => navigate(isDoctor ? `/medico/prontuarios/${medicalRecord.id}` : `/medical-records/${medicalRecord.id}`)} className="mt-4">Visualizar Prontuário</Button>
        </div>
      </div>
    );
  }

  const stepComponents: Record<MedicalRecordStep, ReactNode> = {
    identification: (
      <div className="space-y-4">
        <h3 className="text-lg font-semibold">Identificação do Paciente</h3>
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-gray-50 rounded-lg p-4"><span className="text-sm text-gray-500">Paciente</span><p className="text-lg font-medium mt-1">{medicalRecord.patientId}</p></div>
          <div className="bg-gray-50 rounded-lg p-4"><span className="text-sm text-gray-500">Médico Responsável</span><p className="text-lg font-medium mt-1">{medicalRecord.doctorId}</p></div>
        </div>
        <div className="bg-gray-50 rounded-lg p-4"><span className="text-sm text-gray-500">Appointment</span><p className="text-lg font-medium mt-1">{medicalRecord.appointmentId}</p></div>
        <div className="flex justify-end"><Button onClick={() => { markStepComplete('identification'); setCurrentStep('complaint'); }}>Próximo: Queixa Principal</Button></div>
      </div>
    ),
    complaint: (
      <div className="space-y-4">
        <h3 className="text-lg font-semibold">Queixa Principal e História</h3>
        <p className="text-sm text-gray-500">Registre a queixa principal e a história da doença atual.</p>
        <div className="bg-gray-50 rounded-lg p-4">
          <SOAPEditor initialData={{ subjective: '' }}
            onSave={handleSaveComplaint}
          />
        </div>
      </div>
    ),
    anamnese: (
      <div className="space-y-4">
        <h3 className="text-lg font-semibold">Anamnese</h3>
        <p className="text-sm text-gray-500">Complete a anamnese do paciente.</p>
        <div className="bg-gray-50 rounded-lg p-4">
          <SOAPEditor
            initialData={{ subjective: '', objective: '' }}
            onSave={handleSaveAnamnese}
          />
        </div>
      </div>
    ),
    'physical-exam': (
      <div className="space-y-4">
        <h3 className="text-lg font-semibold">Exame Físico</h3>
        <p className="text-sm text-gray-500">Registre os achados do exame físico.</p>
        <div className="bg-gray-50 rounded-lg p-4">
          <SOAPEditor
            initialData={{ objective: '' }}
            onSave={handleSavePhysicalExam}
          />
        </div>
      </div>
    ),
    diagnoses: (
      <div className="space-y-4">
        <h3 className="text-lg font-semibold">Diagnósticos</h3>
        <DiagnosisSelector medicalRecordId={medicalRecord.id} onDiagnosisAdded={() => markStepComplete('diagnoses')} />
        <div className="flex justify-end mt-4"><Button onClick={() => { markStepComplete('diagnoses'); setCurrentStep('prescriptions'); }}>Próximo: Prescrições</Button></div>
      </div>
    ),
    prescriptions: (
      <div className="space-y-4">
        <h3 className="text-lg font-semibold">Prescrições</h3>
        <PrescriptionEditor
          medicalRecordId={medicalRecord.id}
          patientId={medicalRecord.patientId}
          doctorId={medicalRecord.doctorId}
          onPrescriptionAdded={() => markStepComplete('prescriptions')}
        />
        <div className="flex justify-end mt-4"><Button onClick={() => { markStepComplete('prescriptions'); setCurrentStep('exams'); }}>Próximo: Exames</Button></div>
      </div>
    ),
    exams: (
      <div className="space-y-4">
        <h3 className="text-lg font-semibold">Solicitação de Exames</h3>
        <ExamRequestEditor
          medicalRecordId={medicalRecord.id}
          patientId={medicalRecord.patientId}
          doctorId={medicalRecord.doctorId}
          appointmentId={medicalRecord.appointmentId}
          onExamRequestAdded={() => markStepComplete('exams')}
        />
        <div className="flex justify-end mt-4"><Button onClick={() => { markStepComplete('exams'); setCurrentStep('evolution'); }}>Próximo: Evolução</Button></div>
      </div>
    ),
    evolution: (
      <div className="space-y-4">
        <h3 className="text-lg font-semibold">Evolução Clínica</h3>
        <ClinicalEvolutionEditor
          medicalRecordId={medicalRecord.id}
          appointmentId={medicalRecord.appointmentId}
          doctorId={medicalRecord.doctorId}
          onEvolutionAdded={() => markStepComplete('evolution')}
        />
        <div className="flex justify-end mt-4"><Button onClick={() => { markStepComplete('evolution'); setCurrentStep('summary'); }}>Próximo: Resumo</Button></div>
      </div>
    ),
    summary: (
      <div className="space-y-6">
        <h3 className="text-lg font-semibold">Resumo do Prontuário</h3>
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-green-50 rounded-lg p-4">
            <p className="text-sm text-green-700 font-medium">Diagnósticos</p>
            <p className="text-2xl font-bold text-green-900">{medicalRecord.diagnosisIds?.length || 0}</p>
          </div>
          <div className="bg-blue-50 rounded-lg p-4">
            <p className="text-sm text-blue-700 font-medium">Prescrições</p>
            <p className="text-2xl font-bold text-blue-900">{medicalRecord.prescriptionIds?.length || 0}</p>
          </div>
          <div className="bg-purple-50 rounded-lg p-4">
            <p className="text-sm text-purple-700 font-medium">Exames</p>
            <p className="text-2xl font-bold text-purple-900">{medicalRecord.examRequestIds?.length || 0}</p>
          </div>
          <div className="bg-indigo-50 rounded-lg p-4">
            <p className="text-sm text-indigo-700 font-medium">Evoluções</p>
            <p className="text-2xl font-bold text-indigo-900">{medicalRecord.evolutionIds?.length || 0}</p>
          </div>
        </div>
        <p className="text-sm text-gray-500 mt-4">Após finalizar, você pode assinar ou fechar o prontuário na página de visualização.</p>
        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={() => navigate(isDoctor ? `/medico/prontuarios/${medicalRecord.id}` : `/medical-records/${medicalRecord.id}`)}>
            Ir para Visualização
          </Button>
          <Button onClick={handleClose} disabled={closeRecord.isPending}>
            {closeRecord.isPending ? 'Fechando...' : 'Finalizar e Fechar'}
          </Button>
        </div>
      </div>
    ),
  };

  return (
    <div className="container max-w-4xl py-6">
      <button onClick={() => navigate(isDoctor ? `/medico/prontuarios/${medicalRecord.id}` : `/medical-records/${medicalRecord.id}`)} className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6">
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        Voltar para o Prontuário
      </button>

      <div className="space-y-6">
        <MedicalRecordStepper
          currentStep={currentStep}
          onStepChange={setCurrentStep}
          completedSteps={completedSteps}
        />

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          {stepComponents[currentStep]}
        </div>
      </div>
    </div>
  );
}
